// ============================================
// Demo Data Source — ChatDataSource (mode: 'demo')
//
// Static implementation using real MF-typed data.
// Scenario: Female, 38, fatigue + low labs.
//
// Every value conforms to the same TypeScript
// interfaces as live Master Formulator output.
// ============================================

import type { BiomarkerResult, LabReport } from '../../types';
import type {
  ChatDataSource,
  ChatMode,
  LabAnalystEvent,
} from '../../types/analystBar';
import type {
  CapturedSignal,
  CaptureCategory,
} from '../../types/fashionPlate';
import type {
  AnalystPhase2Output,
  ModifierSet,
  ProtocolItem,
} from '../../types/patientJourney';
import { geminiChat } from '../ai/providers/gemini';
import type { GeminiMessage } from '../ai/providers/gemini';
import { DEFAULT_BRAND_DISPLAY } from '../firestore/brandDisplayService';
import { mapBiomarkerToBarMarker, mapReportToPhase1 } from '../agents/labAnalystStream';

// ── Timing helpers ──

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// =============================================
// 1. DEMO BIOMARKERS
// =============================================

export const DEMO_BIOMARKERS: BiomarkerResult[] = [
  // --- ATTENTION (low) ---
  {
    biomarker: 'TSH',
    value: 3.8,
    unit: 'mIU/L',
    standardRange: { low: 0.4, high: 4.5 },
    functionalRange: { low: 1.0, high: 2.0 },
    eliteAverage: 1.4,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'thyroid',
    interpretation:
      'TSH 3.8 mIU/L is within standard range but above the functional optimal ceiling of 2.0. This suggests subclinical thyroid sluggishness contributing to fatigue and low energy.',
  },
  {
    biomarker: 'Vitamin D (25-OH)',
    value: 28,
    unit: 'ng/mL',
    standardRange: { low: 30, high: 100 },
    functionalRange: { low: 50, high: 80 },
    eliteAverage: 62,
    standardStatus: 'abnormal',
    hemologixStatus: 'low',
    category: 'nutritional',
    interpretation:
      'Vitamin D at 28 ng/mL is below standard range and significantly below functional optimal (50-80). Deficiency at this level impairs immune function, mood, and calcium metabolism.',
  },
  {
    biomarker: 'Ferritin',
    value: 22,
    unit: 'ng/mL',
    standardRange: { low: 12, high: 150 },
    functionalRange: { low: 50, high: 100 },
    eliteAverage: 70,
    standardStatus: 'normal',
    hemologixStatus: 'low',
    category: 'nutritional',
    interpretation:
      'Ferritin at 22 ng/mL is technically "normal" but well below the functional threshold of 50 ng/mL. This level of depleted iron stores is a primary driver of fatigue in menstruating women.',
  },

  // --- BORDERLINE ---
  {
    biomarker: 'Free T3',
    value: 2.8,
    unit: 'pg/mL',
    standardRange: { low: 2.0, high: 4.4 },
    functionalRange: { low: 3.0, high: 4.0 },
    eliteAverage: 3.5,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'thyroid',
    interpretation:
      'Free T3 at 2.8 pg/mL is below the functional floor of 3.0, suggesting impaired T4-to-T3 conversion. Selenium is a required cofactor for deiodinase enzymes.',
  },
  {
    biomarker: 'Estradiol (day 3)',
    value: 38,
    unit: 'pg/mL',
    standardRange: { low: 12.5, high: 166 },
    functionalRange: { low: 50, high: 100 },
    eliteAverage: 75,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'hormonal',
    interpretation:
      'Estradiol at 38 pg/mL falls below the functional optimal floor of 50. In a premenopausal woman this may indicate early ovarian decline or anovulatory cycles.',
  },
  {
    biomarker: 'B12',
    value: 410,
    unit: 'pg/mL',
    standardRange: { low: 200, high: 900 },
    functionalRange: { low: 500, high: 800 },
    eliteAverage: 600,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'nutritional',
    interpretation:
      'B12 at 410 pg/mL is within standard range but below functional optimal of 500. Suboptimal B12 can contribute to fatigue, brain fog, and impaired methylation.',
  },
  {
    biomarker: 'Magnesium (RBC)',
    value: 4.8,
    unit: 'mg/dL',
    standardRange: { low: 4.2, high: 6.8 },
    functionalRange: { low: 5.5, high: 6.5 },
    eliteAverage: 6.0,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'nutritional',
    interpretation:
      'RBC Magnesium at 4.8 mg/dL is below the functional floor of 5.5. Low magnesium impairs sleep quality, muscle recovery, and is a required cofactor for Vitamin D metabolism.',
  },
  {
    biomarker: 'DHEA-S',
    value: 145,
    unit: '\u00b5g/dL',
    standardRange: { low: 35, high: 430 },
    functionalRange: { low: 150, high: 350 },
    eliteAverage: 250,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'hormonal',
    interpretation:
      'DHEA-S at 145 \u00b5g/dL is just below the functional floor of 150. DHEA-S is a precursor to both estrogen and testosterone and declines with chronic stress.',
  },

  // --- OPTIMAL ---
  {
    biomarker: 'Fasting Glucose',
    value: 88,
    unit: 'mg/dL',
    standardRange: { low: 65, high: 99 },
    functionalRange: { low: 70, high: 85 },
    eliteAverage: 80,
    standardStatus: 'normal',
    hemologixStatus: 'optimal',
    category: 'metabolic',
    interpretation:
      'Fasting glucose at 88 mg/dL is within standard range and near optimal. No metabolic intervention needed.',
  },
  {
    biomarker: 'Hemoglobin A1c',
    value: 5.1,
    unit: '%',
    standardRange: { low: 4.0, high: 5.6 },
    functionalRange: { low: 4.5, high: 5.2 },
    eliteAverage: 5.0,
    standardStatus: 'normal',
    hemologixStatus: 'optimal',
    category: 'metabolic',
    interpretation:
      'HbA1c at 5.1% is well within both standard and functional ranges. No glycemic concern.',
  },
  {
    biomarker: 'hs-CRP',
    value: 0.8,
    unit: 'mg/L',
    standardRange: { low: 0, high: 3.0 },
    functionalRange: { low: 0, high: 0.5 },
    eliteAverage: 0.3,
    standardStatus: 'normal',
    hemologixStatus: 'suboptimal',
    category: 'inflammatory',
    interpretation:
      'hs-CRP at 0.8 mg/L is within standard range but above the functional ceiling of 0.5. Mild systemic inflammation may be present.',
  },
  {
    biomarker: 'WBC',
    value: 6.2,
    unit: 'x10\u00b3/\u00b5L',
    standardRange: { low: 4.5, high: 11.0 },
    functionalRange: { low: 5.0, high: 8.0 },
    eliteAverage: 6.0,
    standardStatus: 'normal',
    hemologixStatus: 'optimal',
    category: 'hematologic',
    interpretation:
      'WBC at 6.2 is within both standard and functional ranges. No immune or inflammatory concern.',
  },
];

// =============================================
// 2. DEMO PROTOCOL (Gate 1 — Seed Supplements)
// =============================================

// Real prices from SEED_SUPPLEMENTS formulary:
// Vitamin D3 + K2  → $22/mo (supp-vitamin-d3-k2)
// Iron Bisglycinate → $18/mo (supp-iron-bisglycinate)
// Selenium          → $14/mo (supp-selenium)
// Total: $54/mo → $1.80/day

const DEMO_PROTOCOL_ITEMS: ProtocolItem[] = [
  {
    productId: 'supp-vitamin-d3-k2',
    productName: 'Vitamin D3 + K2',
    dosage: '1 softgel daily (AM with fat-containing meal)',
    timing: 'am_energy',
    reasoning:
      'Vitamin D at 28 ng/mL is below standard range and significantly below functional optimal (50-80). D3 5000 IU with K2 directs calcium to bone, not arteries.',
    triggerBiomarker: 'Vitamin D (25-OH)',
    tier: 1,
  },
  {
    productId: 'supp-iron-bisglycinate',
    productName: 'Iron Bisglycinate',
    dosage: '1 cap daily (midday, 2 hrs away from calcium/coffee/tea)',
    timing: 'midday_absorption',
    reasoning:
      'Ferritin at 22 ng/mL is below functional threshold of 50. Bisglycinate form has 3-4x better absorption than ferrous sulfate with less GI distress.',
    triggerBiomarker: 'Ferritin',
    tier: 1,
  },
  {
    productId: 'supp-selenium',
    productName: 'Selenium',
    dosage: '1 cap daily (AM with food)',
    timing: 'am_energy',
    reasoning:
      'Free T3 at 2.8 pg/mL suggests impaired T4\u2192T3 conversion. Selenium (200 mcg selenomethionine) is the required cofactor for deiodinase enzymes.',
    triggerBiomarker: 'Free T3',
    tier: 1,
  },
];

export const DEMO_PROTOCOL: AnalystPhase2Output = {
  protocol: DEMO_PROTOCOL_ITEMS,
  blockedProducts: [],
  warnings: [],
  modifiersApplied: {
    allergies: [],
    medications: [],
    conditions: [],
    lifestyle: [],
    budgetTier: 'all_tiers',
    intent: 'exploring',
    reproductiveStatus: 'not_applicable',
    priorSupplements: [],
    contraindications: [],
    goals: [],
  },
  gateLevel: 1,
};

// =============================================
// 3. DEMO LAB REPORT (for mapReportToPhase1)
// =============================================

const DEMO_LAB_REPORT: LabReport = {
  id: 'demo-report-001',
  uploadedAt: new Date().toISOString(),
  source: 'pdf',
  biomarkers: DEMO_BIOMARKERS,
};

// =============================================
// 4. SYMPTOM EXTRACTION (keyword-based)
// =============================================

interface SymptomPattern {
  regex: RegExp;
  term: string;
  category: CaptureCategory;
  linkedBiomarker?: string;
}

const SYMPTOM_PATTERNS: SymptomPattern[] = [
  {
    regex: /\b(exhausted|tired)\b/i,
    term: 'Fatigue',
    category: 'symptom',
    linkedBiomarker: 'Ferritin',
  },
  {
    regex: /\b(terrible)\b/i,
    term: 'Low Energy',
    category: 'symptom',
    linkedBiomarker: 'TSH',
  },
  {
    regex: /\b(can'?t\s+sleep)\b/i,
    term: 'Poor Sleep',
    category: 'symptom',
  },
  {
    regex: /\b(brain\s*fog)\b/i,
    term: 'Brain Fog',
    category: 'symptom',
    linkedBiomarker: 'Ferritin',
  },
];

function extractSymptomsFromText(
  msg: string,
  turn: number,
  existing: CapturedSignal[],
): CapturedSignal[] {
  const existingTerms = new Set(existing.map((s) => s.term));
  const newSignals: CapturedSignal[] = [];

  for (const pattern of SYMPTOM_PATTERNS) {
    const match = msg.match(pattern.regex);
    if (!match) continue;

    // Reinforce existing signal instead of duplicating
    const existingSignal = existing.find((s) => s.term === pattern.term);
    if (existingSignal) {
      if (!existingSignal.reinforcedAtTurns.includes(turn)) {
        existingSignal.reinforcedAtTurns.push(turn);
      }
      continue;
    }

    if (existingTerms.has(pattern.term)) continue;

    const signal: CapturedSignal = {
      id: `demo-${pattern.term.toLowerCase().replace(/\s+/g, '-')}-${turn}`,
      term: pattern.term,
      category: pattern.category,
      source: 'patient_mention',
      confidence: 0.95,
      detectedAtTurn: turn,
      reinforcedAtTurns: [],
      sourceText: match[1] || match[0],
      ...(pattern.linkedBiomarker
        ? { linkedBiomarker: pattern.linkedBiomarker }
        : {}),
    };

    newSignals.push(signal);
    existingTerms.add(pattern.term);
  }

  return [...existing, ...newSignals];
}

// =============================================
// 5. LAB ANALYSIS GENERATOR
// =============================================

async function* demoAnalyzeLabs(
  _file: File,
): AsyncGenerator<LabAnalystEvent> {
  // 1. Status: analysis starting
  yield { type: 'status', message: 'Analyzing biomarkers against functional optimal ranges' };
  await delay(300);

  // 2. Yield each biomarker as a BarMarker with pacing
  for (const bio of DEMO_BIOMARKERS) {
    const marker = mapBiomarkerToBarMarker(bio);
    yield { type: 'marker', marker };

    // Attention-flagged markers get a longer pause for dramatic effect
    await delay(marker.flag === 'attention' ? 200 : 120);
  }

  // 3. Yield the complete Phase 1 output
  yield { type: 'complete', report: mapReportToPhase1(DEMO_LAB_REPORT) };
}

// =============================================
// 6. EXPORTED ChatDataSource
// =============================================

export const demoDataSource: ChatDataSource = {
  mode: 'demo' as ChatMode,

  brandDisplay: { ...DEFAULT_BRAND_DISPLAY },

  extractSymptoms: (
    msg: string,
    turn: number,
    existing: CapturedSignal[],
  ): Promise<CapturedSignal[]> => {
    return Promise.resolve(extractSymptomsFromText(msg, turn, existing));
  },

  analyzeLabs: (file: File, _signals?: CapturedSignal[]): AsyncGenerator<LabAnalystEvent> => {
    return demoAnalyzeLabs(file);
  },

  getProtocol: (
    _biomarkers: BiomarkerResult[],
    _modifiers: ModifierSet,
  ): AnalystPhase2Output => {
    // Demo mode returns the pre-built protocol regardless of input
    return DEMO_PROTOCOL;
  },

  nurseRespond: (
    history: GeminiMessage[],
    systemPrompt: string,
    userMsg: string,
  ): Promise<string> => {
    return geminiChat('gemini-2.5-flash', systemPrompt, history, userMsg, 0.7, 512);
  },

  detectLateReveal: (
    _msg: string,
    _turn: number,
  ) => {
    // Demo mode has fixed script — no late reveals possible
    return null;
  },

  getNurseReinforcement: () => {
    // Demo mode uses fixed script — no reinforcement injection
    return '';
  },
};
